import React, { useState, useEffect, useRef } from 'react';
import {
  FaTimes, FaSave, FaMoneyBillWave, FaCalendarAlt,
  FaUser, FaAlignLeft, FaTag, FaCreditCard,
  FaStickyNote, FaUniversity, FaCheck, FaLock,
  FaListUl, FaSpinner, FaCheckCircle, FaLightbulb,
  FaFileInvoiceDollar, FaPaperclip, FaExternalLinkAlt,
  FaTruck, FaParking, FaPen,
} from 'react-icons/fa';
import api from '../services/api';
import { toast } from 'react-toastify';
import './ModalContasAReceber.css';

/* ─── Helpers ─────────────────────────────────────────────── */
const fmtCur = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).split('T')[0].split('-');
  return `${d}/${m}/${y}`;
};

const todayISO = () => new Date().toISOString().split('T')[0];

/* ─── Modal ───────────────────────────────────────────────── */
/*
 * modo:
 *   undefined / 'baixa'  → comportamento atual (registrar recebimento / ver detalhes)
 *   'editar'             → formulário de edição da conta (PUT)
 *   (sem contaToEdit)    → formulário de nova receita (POST)
 */
const ModalContasAReceber = ({ isOpen, onClose, contaToEdit, onSave, modo }) => {
  const [bancos,        setBancos]        = useState([]);
  const [categorias,    setCategorias]    = useState([]);
  const [planoContas,   setPlanoContas]   = useState([]);
  const [currentUser,   setCurrentUser]   = useState(null);
  const [loadingData,   setLoadingData]   = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingIA,     setLoadingIA]     = useState(false);
  const [success,       setSuccess]       = useState(false);

  /* Dados da baixa (quando contaToEdit é um item existente) */
  const [baixaData, setBaixaData] = useState({
    banco_destino_id: '',
    data_recebimento: todayISO(),
  });

  /* Dados do lançamento (compartilhado entre Nova Receita e Edição) */
  const [novaReceita, setNovaReceita] = useState({
    descricao:       '',
    valor:           '',
    data_vencimento: todayISO(),
    cliente_nome:    '',
    categoria_id:    '',
    plano_contas_id: '',
    forma_pagamento: 'boleto',
    observacoes:     '',
    numero_nota:     '',
  });

  /* Upload de anexo */
  const [anexoFile, setAnexoFile] = useState(null);
  const fileInputRef = useRef(null);

  /* ── Modo de operação ──
     A edição só é válida para contas que ainda NÃO foram recebidas
     nem canceladas (o backend rejeita esses casos no PUT). */
  const isEditar = !!contaToEdit
    && modo === 'editar'
    && contaToEdit.status !== 'recebido'
    && contaToEdit.status !== 'cancelado';
  const isBaixa  = !!contaToEdit && !isEditar;
  const isCriar  = !contaToEdit;

  /* ── Carrega dados auxiliares ao abrir ── */
  useEffect(() => {
    if (!isOpen) { setSuccess(false); return; }

    setLoadingData(true);
    try { setCurrentUser(JSON.parse(localStorage.getItem('user') || '{}')); } catch {}

    Promise.all([
      api.get('/api/financeiro/conexoes').catch(() => ({ data: [] })),
      api.get('/api/financeiro/categorias-receita').catch(() => ({ data: [] })),
      api.get('/api/financeiro/plano-contas').catch(() => ({ data: [] })),
    ]).then(([rb, rc, rp]) => {
      setBancos((Array.isArray(rb.data) ? rb.data : []).filter(b => b.status === 'conectado' || b.conectado));
      setCategorias(Array.isArray(rc.data) ? rc.data : []);
      setPlanoContas(Array.isArray(rp.data) ? rp.data : []);
    }).finally(() => setLoadingData(false));

    if (!contaToEdit) {
      /* Reset form para nova receita */
      setNovaReceita({
        descricao: '', valor: '', data_vencimento: todayISO(),
        cliente_nome: '', categoria_id: '', plano_contas_id: '',
        forma_pagamento: 'boleto', observacoes: '', numero_nota: '',
      });
      setAnexoFile(null);
    } else {
      /* Mantém dados de baixa zerados */
      setBaixaData({ banco_destino_id: '', data_recebimento: todayISO() });

      /* Pré-preenche o formulário com os dados da conta.
         Usado pelo modo de edição; inofensivo para o modo baixa/ver
         (que não lê novaReceita). */
      setNovaReceita({
        descricao:       contaToEdit.descricao || '',
        valor:           contaToEdit.valor != null ? String(contaToEdit.valor) : '',
        data_vencimento: contaToEdit.data_vencimento
                            ? String(contaToEdit.data_vencimento).split('T')[0]
                            : todayISO(),
        cliente_nome:    contaToEdit.cliente_nome || contaToEdit.cliente_nome_cadastro || '',
        categoria_id:    '',   /* não editável aqui (não persistido pelo PUT) */
        plano_contas_id: '',   /* idem */
        forma_pagamento: String(contaToEdit.forma_pagamento || 'boleto').toLowerCase(),
        observacoes:     contaToEdit.observacoes || '',
        numero_nota:     contaToEdit.numero_nota || '',
      });
      setAnexoFile(null);
    }
  }, [isOpen, contaToEdit, modo]);

  /* ── Mudança de categoria → sugere plano de contas padrão ── */
  const handleCategoriaChange = (e) => {
    const id = e.target.value;
    setNovaReceita(prev => ({ ...prev, categoria_id: id }));
    const cat = categorias.find(c => c.id === parseInt(id));
    if (cat?.plano_contas_id_padrao)
      setNovaReceita(prev => ({ ...prev, plano_contas_id: cat.plano_contas_id_padrao }));
  };

  /* ── IA: Sugerir Plano de Contas ──────────────────────────────
     Enriquece o contexto com numero_nota quando disponível.
     O número da nota passa informação valiosa sobre o tipo de
     documento (NF-e produto vs NFSe serviço), auxiliando o modelo.
  ── */
  const handleSugerirConta = async () => {
    if (!novaReceita.descricao.trim()) {
      toast.warn('Preencha a descrição para usar a sugestão da IA.');
      return;
    }
    setLoadingIA(true);
    try {
      const catNome = categorias.find(c => c.id === parseInt(novaReceita.categoria_id))?.nome;

      /* Contexto enriquecido: descrição + nota fiscal (se preenchida) */
      const descContexto = [
        novaReceita.descricao.trim(),
        novaReceita.numero_nota?.trim()
          ? `[Nota Fiscal: ${novaReceita.numero_nota.trim()}]`
          : '',
      ].filter(Boolean).join(' ');

      const { data } = await api.post('/api/gastos/sugerir-conta', {
        descricao:      descContexto,
        categoria_nome: catNome,
        valor:          novaReceita.valor || 0,
        tipo_movimento: 'receita',
      });

      if (data?.id) {
        setNovaReceita(prev => ({ ...prev, plano_contas_id: data.id }));
        const conf = data.confianca === 'alta' ? '✓ ' : '';
        toast.info(`${conf}IA sugeriu: ${data.nome}${data.justificativa ? ` · ${data.justificativa}` : ''}`);
      }
    } catch {
      toast.error('Não foi possível obter sugestão da IA no momento.');
    } finally {
      setLoadingIA(false);
    }
  };

  /* ── Baixar conta existente ── */
  const handleBaixar = async () => {
    setLoadingSubmit(true);
    try {
      await api.post('/api/contas-a-receber/receber-lote', {
        ids: [contaToEdit.id],
        conta_bancaria_id: baixaData.banco_destino_id || null,
      });
      setSuccess(true);
      setTimeout(() => { onSave?.(); onClose(); }, 1300);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao registrar recebimento.');
    } finally {
      setLoadingSubmit(false);
    }
  };

  /* ── Criar nova receita ── */
  const handleCriar = async (e) => {
    e.preventDefault();
    if (!novaReceita.descricao.trim()) { toast.warn('Descrição é obrigatória.'); return; }
    if (!novaReceita.valor || parseFloat(novaReceita.valor) <= 0) { toast.warn('Valor inválido.'); return; }
    if (!novaReceita.data_vencimento) { toast.warn('Data de vencimento obrigatória.'); return; }

    setLoadingSubmit(true);
    try {
      const fd = new FormData();
      Object.entries(novaReceita).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) fd.append(k, v);
      });
      if (anexoFile) fd.append('anexo', anexoFile);

      await api.post('/api/contas-a-receber', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Receita lançada com sucesso!');
      onSave?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao criar receita.');
    } finally {
      setLoadingSubmit(false);
    }
  };

  /* ── Editar conta existente (PUT) ──────────────────────────────
     Envia JSON (a rota PUT não usa multipart). Campos que o
     formulário não expõe (categoria string, cliente_id) são
     repassados COMO ESTÃO para não serem apagados pelo backend. */
  const handleEditar = async (e) => {
    e.preventDefault();
    if (!novaReceita.descricao.trim()) { toast.warn('Descrição é obrigatória.'); return; }
    if (!novaReceita.valor || parseFloat(novaReceita.valor) <= 0) { toast.warn('Valor inválido.'); return; }
    if (!novaReceita.data_vencimento) { toast.warn('Data de vencimento obrigatória.'); return; }

    setLoadingSubmit(true);
    try {
      await api.put(`/api/contas-a-receber/${contaToEdit.id}`, {
        descricao:       novaReceita.descricao.trim(),
        valor:           parseFloat(novaReceita.valor),
        data_vencimento: novaReceita.data_vencimento,
        /* passthrough — não editáveis neste form, preservados intactos */
        cliente_id:      contaToEdit.cliente_id ?? null,
        categoria:       contaToEdit.categoria ?? null,
        /* editáveis */
        cliente_nome:    novaReceita.cliente_nome?.trim() || null,
        forma_pagamento: novaReceita.forma_pagamento || null,
        observacoes:     novaReceita.observacoes?.trim() || null,
        numero_nota:     novaReceita.numero_nota?.trim() || null,
      });
      toast.success('Conta a receber atualizada!');
      onSave?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao atualizar conta.');
    } finally {
      setLoadingSubmit(false);
    }
  };

  const canEditNatureza = ['admin_geral', 'admin'].includes(currentUser?.perfil);
  const isAlreadyPaid   = contaToEdit?.status === 'recebido';
  const useFormSize     = isCriar || isEditar;
  const isOsOrigin      = isEditar
    && (contaToEdit.origem_tipo === 'os_servico' || contaToEdit.origem_tipo === 'patio');

  if (!isOpen) return null;

  return (
    <div className="mcr-overlay" onClick={onClose}>
      <div
        className={`mcr-panel ${useFormSize ? 'mcr-form-size' : 'mcr-baixa-size'}`}
        onClick={e => e.stopPropagation()}
      >

        {/* ── Header ── */}
        <div className="mcr-header">
          <div className="mcr-header-text">
            <span className="mcr-eyebrow">Contas a Receber</span>
            <h2 className="mcr-title">
              {isEditar
                ? 'Editar Receita'
                : isBaixa
                  ? (isAlreadyPaid ? 'Detalhes do Recebimento' : 'Registrar Recebimento')
                  : 'Nova Receita'}
            </h2>
          </div>
          <button className="mcr-close" onClick={onClose} title="Fechar"><FaTimes /></button>
        </div>

        {/* ── Body ── */}
        <div className="mcr-body">

          {/* ════════════════════════════
              MODO BAIXA / VISUALIZAÇÃO
          ════════════════════════════ */}
          {isBaixa && (
            <div className="mcr-baixa">
              {success ? (
                /* Sucesso */
                <div className="mcr-success">
                  <div className="success-circle"><FaCheckCircle /></div>
                  <h3>Recebimento confirmado!</h3>
                  <p>{fmtCur(contaToEdit.valor)} registrado com sucesso.</p>
                </div>
              ) : (
                <>
                  {/* Recibo resumido */}
                  <div className="mcr-receipt-card">
                    <div className="rcpt-badges">
                      {contaToEdit.status === 'vencido' && (
                        <span className="rcpt-badge rcpt-overdue">Vencido</span>
                      )}
                      {contaToEdit.status === 'pendente' && (
                        <span className="rcpt-badge rcpt-pending">Pendente</span>
                      )}
                      {isAlreadyPaid && (
                        <span className="rcpt-badge rcpt-paid">
                          <FaCheckCircle /> Recebido
                        </span>
                      )}
                      {contaToEdit.origem_tipo === 'os_servico' && (
                        <span className="rcpt-badge rcpt-os">
                          <FaTruck /> OS #{contaToEdit.origem_id}
                        </span>
                      )}
                      {contaToEdit.origem_tipo === 'patio' && (
                        <span className="rcpt-badge rcpt-patio">
                          <FaParking /> Pátio #{contaToEdit.origem_id}
                        </span>
                      )}
                    </div>

                    <div className="rcpt-desc">{contaToEdit.descricao}</div>

                    {/* ── Número da Nota (se existir) ── */}
                    {contaToEdit.numero_nota && (
                      <div className="rcpt-nota">
                        <FaFileInvoiceDollar className="rcpt-icon" />
                        <span>Nota: <strong>{contaToEdit.numero_nota}</strong></span>
                      </div>
                    )}

                    {(contaToEdit.cliente_nome || contaToEdit.cliente_nome_cadastro) && (
                      <div className="rcpt-client">
                        <FaUser className="rcpt-icon" />
                        <span>{contaToEdit.cliente_nome || contaToEdit.cliente_nome_cadastro}</span>
                      </div>
                    )}

                    <div className="rcpt-amount">{fmtCur(contaToEdit.valor)}</div>

                    <div className="rcpt-due">
                      <FaCalendarAlt className="rcpt-icon" />
                      <span>Vencimento: {fmtDate(contaToEdit.data_vencimento)}</span>
                    </div>

                    {isAlreadyPaid && contaToEdit.data_recebimento && (
                      <div className="rcpt-due rcpt-paid-date">
                        <FaCheck className="rcpt-icon" />
                        <span>Recebido em: {fmtDate(contaToEdit.data_recebimento)}</span>
                      </div>
                    )}

                    {/* ── Anexo (se existir) ── */}
                    {contaToEdit.anexo_url && (
                      <a
                        href={`https://drive.google.com/file/d/${contaToEdit.anexo_url}/view`}
                        target="_blank"
                        rel="noreferrer"
                        className="rcpt-anexo-link"
                      >
                        <FaPaperclip /> Ver comprovante
                        <FaExternalLinkAlt size={9} />
                      </a>
                    )}
                  </div>

                  {/* Formulário de baixa (apenas para itens não pagos) */}
                  {!isAlreadyPaid && (
                    <>
                      <div className="mcr-fields">
                        <div className="mcr-field">
                          <label><FaUniversity /> Conta Destino</label>
                          <select
                            value={baixaData.banco_destino_id}
                            onChange={e => setBaixaData(p => ({ ...p, banco_destino_id: e.target.value }))}
                          >
                            <option value="">🏦 Caixa Principal</option>
                            {bancos.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                          </select>
                        </div>
                        <div className="mcr-field">
                          <label><FaCalendarAlt /> Data do Crédito</label>
                          <input
                            type="date"
                            value={baixaData.data_recebimento}
                            max={todayISO()}
                            onChange={e => setBaixaData(p => ({ ...p, data_recebimento: e.target.value }))}
                          />
                        </div>
                      </div>

                      <button
                        className="mcr-confirm-btn"
                        onClick={handleBaixar}
                        disabled={loadingSubmit}
                      >
                        {loadingSubmit
                          ? <><FaSpinner className="btn-spin" /> Processando…</>
                          : <><FaCheck /> Confirmar Recebimento de {fmtCur(contaToEdit.valor)}</>}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ════════════════════════════
              MODO EDIÇÃO
          ════════════════════════════ */}
          {isEditar && (
            <form className="mcr-form" onSubmit={handleEditar} noValidate>

              {/* Aviso para contas originadas de OS / Pátio */}
              {isOsOrigin && (
                <div
                  className="mcr-edit-osnote"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    margin: '0 0 14px', padding: '10px 14px',
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: 8, color: '#1e3a8a', fontSize: '0.82rem',
                    lineHeight: 1.4,
                  }}
                >
                  {contaToEdit.origem_tipo === 'patio' ? <FaParking /> : <FaTruck />}
                  <span>
                    Conta originada de{' '}
                    <strong>
                      {contaToEdit.origem_tipo === 'patio' ? 'Pátio' : 'OS'} #{contaToEdit.origem_id}
                    </strong>. Alterar o valor aqui <strong>não</strong> altera a OS de origem.
                  </span>
                </div>
              )}

              <div className="mcr-form-grid">

                {/* Descrição */}
                <div className="mcr-field mcr-span-2">
                  <label><FaAlignLeft /> DESCRIÇÃO <span className="req">*</span></label>
                  <input
                    type="text"
                    placeholder="Ex: Remoção Pesada · Mensalidade · Serviço"
                    value={novaReceita.descricao}
                    onChange={e => setNovaReceita(p => ({ ...p, descricao: e.target.value }))}
                    required
                    autoFocus
                  />
                </div>

                {/* Cliente */}
                <div className="mcr-field mcr-span-2">
                  <label><FaUser /> CLIENTE</label>
                  <input
                    type="text"
                    placeholder="Nome do cliente ou empresa"
                    value={novaReceita.cliente_nome}
                    onChange={e => setNovaReceita(p => ({ ...p, cliente_nome: e.target.value }))}
                  />
                </div>

                {/* Valor */}
                <div className="mcr-field">
                  <label><FaMoneyBillWave /> VALOR <span className="req">*</span></label>
                  <div className="mcr-input-currency">
                    <span className="currency-pfx">R$</span>
                    <input
                      type="number" step="0.01" min="0.01"
                      placeholder="0,00"
                      value={novaReceita.valor}
                      onChange={e => setNovaReceita(p => ({ ...p, valor: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                {/* Vencimento */}
                <div className="mcr-field">
                  <label><FaCalendarAlt /> VENCIMENTO <span className="req">*</span></label>
                  <input
                    type="date"
                    value={novaReceita.data_vencimento}
                    onChange={e => setNovaReceita(p => ({ ...p, data_vencimento: e.target.value }))}
                    required
                  />
                </div>

                {/* Forma de Pagamento */}
                <div className="mcr-field">
                  <label><FaCreditCard /> FORMA DE PAGAMENTO</label>
                  <select
                    value={novaReceita.forma_pagamento}
                    onChange={e => setNovaReceita(p => ({ ...p, forma_pagamento: e.target.value }))}
                  >
                    <option value="boleto">Boleto</option>
                    <option value="pix">Pix</option>
                    <option value="cartao">Cartão</option>
                    <option value="faturamento">Faturamento</option>
                    <option value="deposito">Depósito</option>
                    <option value="dinheiro">Dinheiro</option>
                  </select>
                </div>

                {/* Nº da Nota Fiscal */}
                <div className="mcr-field">
                  <label>
                    <FaFileInvoiceDollar /> Nº DA NOTA
                    <span className="mcr-opt"> (opcional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="NF-e 0000 · NFSe · Recibo 000"
                    value={novaReceita.numero_nota}
                    onChange={e => setNovaReceita(p => ({ ...p, numero_nota: e.target.value }))}
                  />
                </div>

                {/* Observações */}
                <div className="mcr-field mcr-span-4">
                  <label><FaStickyNote /> OBSERVAÇÕES</label>
                  <textarea
                    placeholder="Informações adicionais, referências, número do contrato…"
                    value={novaReceita.observacoes}
                    onChange={e => setNovaReceita(p => ({ ...p, observacoes: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="mcr-footer">
                <button type="button" className="mcr-btn-cancel" onClick={onClose} disabled={loadingSubmit}>
                  Cancelar
                </button>
                <button type="submit" className="mcr-btn-save" disabled={loadingSubmit}>
                  {loadingSubmit
                    ? <><FaSpinner className="btn-spin" /> Salvando…</>
                    : <><FaPen /> Salvar Alterações</>}
                </button>
              </div>
            </form>
          )}

          {/* ════════════════════════════
              MODO NOVA RECEITA
          ════════════════════════════ */}
          {isCriar && (
            <form className="mcr-form" onSubmit={handleCriar} noValidate>
              <div className="mcr-form-grid">

                {/* Descrição */}
                <div className="mcr-field mcr-span-2">
                  <label><FaAlignLeft /> DESCRIÇÃO <span className="req">*</span></label>
                  <input
                    type="text"
                    placeholder="Ex: Remoção Pesada · Mensalidade · Serviço"
                    value={novaReceita.descricao}
                    onChange={e => setNovaReceita(p => ({ ...p, descricao: e.target.value }))}
                    required
                    autoFocus
                  />
                </div>

                {/* Cliente */}
                <div className="mcr-field mcr-span-2">
                  <label><FaUser /> CLIENTE</label>
                  <input
                    type="text"
                    placeholder="Nome do cliente ou empresa"
                    value={novaReceita.cliente_nome}
                    onChange={e => setNovaReceita(p => ({ ...p, cliente_nome: e.target.value }))}
                  />
                </div>

                {/* Valor */}
                <div className="mcr-field">
                  <label><FaMoneyBillWave /> VALOR <span className="req">*</span></label>
                  <div className="mcr-input-currency">
                    <span className="currency-pfx">R$</span>
                    <input
                      type="number" step="0.01" min="0.01"
                      placeholder="0,00"
                      value={novaReceita.valor}
                      onChange={e => setNovaReceita(p => ({ ...p, valor: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                {/* Vencimento */}
                <div className="mcr-field">
                  <label><FaCalendarAlt /> VENCIMENTO <span className="req">*</span></label>
                  <input
                    type="date"
                    value={novaReceita.data_vencimento}
                    onChange={e => setNovaReceita(p => ({ ...p, data_vencimento: e.target.value }))}
                    required
                  />
                </div>

                {/* Forma de Pagamento */}
                <div className="mcr-field">
                  <label><FaCreditCard /> FORMA DE PAGAMENTO</label>
                  <select
                    value={novaReceita.forma_pagamento}
                    onChange={e => setNovaReceita(p => ({ ...p, forma_pagamento: e.target.value }))}
                  >
                    <option value="boleto">Boleto</option>
                    <option value="pix">Pix</option>
                    <option value="cartao">Cartão</option>
                    <option value="faturamento">Faturamento</option>
                    <option value="deposito">Depósito</option>
                    <option value="dinheiro">Dinheiro</option>
                  </select>
                </div>

                {/* ── Nº da Nota Fiscal (NOVO) ── */}
                <div className="mcr-field">
                  <label>
                    <FaFileInvoiceDollar /> Nº DA NOTA
                    <span className="mcr-opt"> (opcional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="NF-e 0000 · NFSe · Recibo 000"
                    value={novaReceita.numero_nota}
                    onChange={e => setNovaReceita(p => ({ ...p, numero_nota: e.target.value }))}
                  />
                </div>

                {/* ── Upload de Anexo (NOVO) ── */}
                <div className="mcr-field">
                  <label>
                    <FaPaperclip /> COMPROVANTE / NF
                    <span className="mcr-opt"> (opcional)</span>
                  </label>
                  <div className="mcr-file-wrap">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={e => setAnexoFile(e.target.files[0] || null)}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      className="mcr-file-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FaPaperclip />
                      {anexoFile
                        ? <span className="mcr-file-name">{anexoFile.name}</span>
                        : 'Selecionar arquivo'}
                    </button>
                    {anexoFile && (
                      <button
                        type="button"
                        className="mcr-file-clear"
                        title="Remover arquivo"
                        onClick={() => {
                          setAnexoFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>

                {/* Divisor classificação contábil */}
                <div className="mcr-divider mcr-span-4">
                  <span>Classificação contábil</span>
                </div>

                {/* Categoria Operacional */}
                <div className="mcr-field mcr-span-2">
                  <label><FaTag /> CATEGORIA OPERACIONAL</label>
                  <select value={novaReceita.categoria_id} onChange={handleCategoriaChange}>
                    <option value="">— Selecione —</option>
                    {categorias.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>

                {/* Natureza Contábil (+ botão IA) */}
                <div className="mcr-field mcr-span-2">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      <FaListUl /> NATUREZA CONTÁBIL
                      {!canEditNatureza && <FaLock style={{ marginLeft: 5, opacity: 0.5, fontSize: '0.7rem' }} />}
                    </span>
                    <button
                      type="button"
                      className={`mcr-ia-trigger ${novaReceita.numero_nota ? 'has-nota' : ''}`}
                      onClick={handleSugerirConta}
                      disabled={loadingIA || !canEditNatureza}
                      title={
                        novaReceita.numero_nota
                          ? `Sugerir com IA · contexto: descrição + nota ${novaReceita.numero_nota}`
                          : 'Sugerir natureza contábil com IA'
                      }
                    >
                      {loadingIA ? <FaSpinner className="btn-spin" /> : <FaLightbulb />}
                      {novaReceita.numero_nota ? 'IA + Nota' : 'IA'}
                    </button>
                  </label>
                  <select
                    value={novaReceita.plano_contas_id}
                    onChange={e => setNovaReceita(p => ({ ...p, plano_contas_id: e.target.value }))}
                    disabled={!canEditNatureza}
                    className={!canEditNatureza ? 'field-locked' : ''}
                  >
                    <option value="">— Enquadramento contábil —</option>
                    {planoContas.map(c => (
                      <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>
                    ))}
                  </select>
                </div>

                {/* Observações */}
                <div className="mcr-field mcr-span-4">
                  <label><FaStickyNote /> OBSERVAÇÕES</label>
                  <textarea
                    placeholder="Informações adicionais, referências, número do contrato…"
                    value={novaReceita.observacoes}
                    onChange={e => setNovaReceita(p => ({ ...p, observacoes: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="mcr-footer">
                <button type="button" className="mcr-btn-cancel" onClick={onClose} disabled={loadingSubmit}>
                  Cancelar
                </button>
                <button type="submit" className="mcr-btn-save" disabled={loadingSubmit}>
                  {loadingSubmit
                    ? <><FaSpinner className="btn-spin" /> Salvando…</>
                    : <><FaSave /> Salvar Lançamento</>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalContasAReceber;